export type WizardType = 'pyromancer' | 'frostmage' | 'archmage' | 'battlemage' | 'necromancer' | 'druid' | 'chronomancer';

export interface WizardConfig {
  type: WizardType;
  name: string;
  description: string;
  icon: string;
  health: number;
  fireballDamage: number;
  fireballCooldown: number;
  iceShardDamage: number;
  iceShardCooldown: number;
  shieldStrength: number;
  shieldCooldown: number;
  shieldDuration: number;
  lightningBoltDamage: number;
  lightningBoltCooldown: number;
  healAmount: number;
  healCooldown: number;
  reflectCooldown: number;
  reflectDuration: number; // ms - how long the barrier stays active
  reflectDamageReturn: number; // fraction of damage returned (0-1)
  teleportCooldown: number;
  teleportInvulnerability: number; // ms of invulnerability after teleport
  // Ultimate spell stats (two-handed casting)
  meteorDamage: number;
  meteorCooldown: number;
  meteorChargeTime: number; // seconds
  superShieldStrength: number;
  superShieldCooldown: number;
  superShieldDuration: number;
  superShieldReflect: number; // fraction of damage reflected back (0-1)
  blizzardDamage: number;
  blizzardCooldown: number;
  blizzardDotDamage: number; // damage per tick over duration
  blizzardDotDuration: number; // ms of DOT effect
  color: string;
}

export const WIZARD_CONFIGS: Record<WizardType, WizardConfig> = {
  pyromancer: {
    type: 'pyromancer',
    name: 'Pyromancer',
    description: 'Glass cannon. Highest damage, faster cooldowns, low HP.',
    icon: '\u{1F525}',
    health: 90,
    fireballDamage: 35,
    fireballCooldown: 4000,  // Faster cooldowns - glass cannon
    iceShardDamage: 18,
    iceShardCooldown: 4200,
    shieldStrength: 20,
    shieldCooldown: 6000,
    shieldDuration: 3000,
    lightningBoltDamage: 30,
    lightningBoltCooldown: 4500,
    healAmount: 8,
    healCooldown: 9000,
    reflectCooldown: 10000,  // Slowest reflect - glass cannon relies on offense
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 7000,  // Shortest teleport - glass cannon needs escape
    teleportInvulnerability: 500,
    meteorDamage: 70,        // Pyromancer has the strongest Meteor
    meteorCooldown: 18000,
    meteorChargeTime: 2,
    superShieldStrength: 40,
    superShieldCooldown: 14000,
    superShieldDuration: 4000,
    superShieldReflect: 0.2,
    blizzardDamage: 20,
    blizzardCooldown: 16000,
    blizzardDotDamage: 5,
    blizzardDotDuration: 4000,
    color: '#FF6B35'
  },
  frostmage: {
    type: 'frostmage',
    name: 'Frost Mage',
    description: 'Ice specialist. Longer slow effects, ice shard chains.',
    icon: '\u{2744}\u{FE0F}',
    health: 100,
    fireballDamage: 20,
    fireballCooldown: 5200,
    iceShardDamage: 26,
    iceShardCooldown: 3500,  // Faster ice shards for chaining
    shieldStrength: 35,
    shieldCooldown: 4500,
    shieldDuration: 6000,
    lightningBoltDamage: 22,
    lightningBoltCooldown: 5500,
    healAmount: 15,
    healCooldown: 7000,
    reflectCooldown: 9000,
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 8000,
    teleportInvulnerability: 500,
    meteorDamage: 45,
    meteorCooldown: 20000,
    meteorChargeTime: 2,
    superShieldStrength: 50,
    superShieldCooldown: 13000,
    superShieldDuration: 4000,
    superShieldReflect: 0.25,
    blizzardDamage: 30,       // Frostmage has the strongest Blizzard
    blizzardCooldown: 15000,
    blizzardDotDamage: 7,
    blizzardDotDuration: 5000,
    color: '#87CEEB'
  },
  archmage: {
    type: 'archmage',
    name: 'Archmage',
    description: 'Balanced mastery. Faster cooldowns, spell variety bonus.',
    icon: '\u{1F9D9}',
    health: 100,
    fireballDamage: 25,
    fireballCooldown: 4500,  // Slightly faster cooldowns across the board
    iceShardDamage: 22,
    iceShardCooldown: 3800,
    shieldStrength: 30,
    shieldCooldown: 4500,
    shieldDuration: 5000,
    lightningBoltDamage: 25,
    lightningBoltCooldown: 4500,
    healAmount: 12,
    healCooldown: 7000,
    reflectCooldown: 8000,  // Fastest reflect - rewards versatility
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 8000,
    teleportInvulnerability: 500,
    meteorDamage: 55,
    meteorCooldown: 17000,   // Archmage has shorter ultimate cooldowns
    meteorChargeTime: 2,
    superShieldStrength: 55,
    superShieldCooldown: 12000,
    superShieldDuration: 4000,
    superShieldReflect: 0.3,
    blizzardDamage: 25,
    blizzardCooldown: 15000,
    blizzardDotDamage: 6,
    blizzardDotDuration: 4000,
    color: '#9B59B6'
  },
  battlemage: {
    type: 'battlemage',
    name: 'Battlemage',
    description: 'Fortified defender. Strongest shields, devastating slow hits.',
    icon: '\u{1F6E1}\u{FE0F}',
    health: 130,
    fireballDamage: 22,
    fireballCooldown: 5500,
    iceShardDamage: 20,
    iceShardCooldown: 4500,
    shieldStrength: 50,
    shieldCooldown: 3500,  // Faster shield cooldown
    shieldDuration: 7000,
    lightningBoltDamage: 20,
    lightningBoltCooldown: 5500,
    healAmount: 20,
    healCooldown: 6000,
    reflectCooldown: 9000,
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 9000,  // Longest teleport - tanks don't need to dodge
    teleportInvulnerability: 500,
    meteorDamage: 45,
    meteorCooldown: 20000,
    meteorChargeTime: 2,
    superShieldStrength: 70,  // Battlemage has the strongest Super Shield
    superShieldCooldown: 12000,
    superShieldDuration: 5000,
    superShieldReflect: 0.35,
    blizzardDamage: 20,
    blizzardCooldown: 18000,
    blizzardDotDamage: 5,
    blizzardDotDuration: 4000,
    color: '#2ECC71'
  },
  necromancer: {
    type: 'necromancer',
    name: 'Necromancer',
    description: 'Dark sustain. Life-steal on hits, damage-over-time curses.',
    icon: '\u{1F480}',
    health: 95,
    fireballDamage: 25,
    fireballCooldown: 4500,
    iceShardDamage: 22,
    iceShardCooldown: 4500,
    shieldStrength: 25,
    shieldCooldown: 5500,
    shieldDuration: 3000,
    lightningBoltDamage: 24,
    lightningBoltCooldown: 4500,
    healAmount: 12,
    healCooldown: 8000,
    reflectCooldown: 9500,
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 8000,
    teleportInvulnerability: 500,
    meteorDamage: 55,
    meteorCooldown: 19000,
    meteorChargeTime: 2,
    superShieldStrength: 35,
    superShieldCooldown: 14000,
    superShieldDuration: 3500,
    superShieldReflect: 0.2,
    blizzardDamage: 25,
    blizzardCooldown: 16000,
    blizzardDotDamage: 7,        // Necromancer excels at DoT
    blizzardDotDuration: 5000,
    color: '#6B2D8B'
  },
  druid: {
    type: 'druid',
    name: 'Druid',
    description: 'Nature healer. Healing zones, root effects, outlasts opponents.',
    icon: '\u{1F33F}',
    health: 110,
    fireballDamage: 18,
    fireballCooldown: 5000,
    iceShardDamage: 20,
    iceShardCooldown: 4200,
    shieldStrength: 40,
    shieldCooldown: 4500,
    shieldDuration: 5500,
    lightningBoltDamage: 18,
    lightningBoltCooldown: 5000,
    healAmount: 25,
    healCooldown: 6500,
    reflectCooldown: 9000,
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 8500,
    teleportInvulnerability: 500,
    meteorDamage: 40,
    meteorCooldown: 20000,
    meteorChargeTime: 2.5,
    superShieldStrength: 60,
    superShieldCooldown: 12000,
    superShieldDuration: 5500,
    superShieldReflect: 0.25,
    blizzardDamage: 22,
    blizzardCooldown: 16000,
    blizzardDotDamage: 5,
    blizzardDotDuration: 4000,
    color: '#4A7C3F'
  },
  chronomancer: {
    type: 'chronomancer',
    name: 'Chronomancer',
    description: 'Time mage. Fastest cooldowns, resets after spell combos.',
    icon: '\u{231B}',
    health: 90,
    fireballDamage: 24,
    fireballCooldown: 3500,
    iceShardDamage: 22,
    iceShardCooldown: 3000,
    shieldStrength: 20,
    shieldCooldown: 5500,
    shieldDuration: 2500,
    lightningBoltDamage: 26,
    lightningBoltCooldown: 4000,
    healAmount: 8,
    healCooldown: 8000,
    reflectCooldown: 9000,
    reflectDuration: 500,
    reflectDamageReturn: 0.8,
    teleportCooldown: 7000,
    teleportInvulnerability: 500,
    meteorDamage: 50,
    meteorCooldown: 15000,       // Chronomancer has fastest ultimate cooldowns
    meteorChargeTime: 1.5,
    superShieldStrength: 35,
    superShieldCooldown: 11000,
    superShieldDuration: 3000,
    superShieldReflect: 0.2,
    blizzardDamage: 22,
    blizzardCooldown: 13000,
    blizzardDotDamage: 5,
    blizzardDotDuration: 3500,
    color: '#4A90D9'
  }
};
